/**
 * Rug Swatches Bundle System
 * Handles expandable footers and drawer functionality for rug swatch selection
 */

const MAX_SELECTIONS = 5;

/**
 * ExpandableFooter Class
 * Manages expandable footer UI for displaying selected swatches
 */
class ExpandableFooter {
  constructor(config) {
    this.prefix = config.prefix;
    this.containerClass = config.containerClass || '';
    this.addToCartCallback = config.addToCartCallback;
    this.hasOpenedOnce = false;

    this.elements = {
      footer: document.querySelector(`.expandable-footer${this.containerClass ? '.' + this.containerClass : ''}`),
      toggle: document.getElementById(`${this.prefix}-footer-toggle`),
      collapse: document.getElementById(`${this.prefix}-footer-collapse`),
      selectedList: document.getElementById(`${this.prefix}-selected-list`),
      emptySlots: document.getElementById(`${this.prefix}-empty-slots`),
      countText: document.getElementById(`${this.prefix}-count-text`),
      selectedCount: document.getElementById(`${this.prefix}-selected-count`),
      footerCount: document.getElementById(`${this.prefix}-footer-count`),
      addBtn: document.getElementById(`${this.prefix}-add-btn`),
      addBtnFooter: document.getElementById(`${this.prefix}-add-btn-footer`)
    };

    this.init();
  }

  init() {
    if (!this.elements.footer) {
      console.error('Expandable footer not found');
      return;
    }

    // Bind toggle listeners
    this.elements.toggle?.addEventListener('click', () => this.toggle());
    this.elements.collapse?.addEventListener('click', () => this.toggle());

    // Register global update function
    const functionName = `update${this.prefix}ExpandableFooter`;
    window[functionName] = (checked, callback) => this.update(checked, callback);
  }

  toggle() {
    const isExpanding = !this.elements.footer.classList.contains('expanded');
    this.elements.footer.classList.toggle('expanded');

    if (isExpanding && !this.hasOpenedOnce) {
      this.elements.footer.classList.add('first-open');
      this.hasOpenedOnce = true;
      setTimeout(() => this.elements.footer.classList.remove('first-open'), 1000);
    }
  }

  update(checked, addToCartCallback) {
    const checkedCount = checked.length;

    // Update count displays
    this.updateCounts(checkedCount);

    // Populate selected list
    this.populateSelectedList(checked);

    // Populate empty slots
    this.populateEmptySlots(checkedCount);

    // Update button states
    this.updateButtons(checkedCount, addToCartCallback);
  }

  updateCounts(count) {
    const countText = `${count}/${MAX_SELECTIONS} SWATCHES SELECTED`;
    if (this.elements.countText) this.elements.countText.textContent = count;
    if (this.elements.selectedCount) this.elements.selectedCount.textContent = countText;
    if (this.elements.footerCount) this.elements.footerCount.textContent = countText;
  }

  populateSelectedList(checked) {
    if (!this.elements.selectedList) return;

    this.elements.selectedList.innerHTML = '';
    checked.forEach(cb => {
      const productTitle = cb.dataset.productTitle || '';
      const variantTitle = cb.dataset.variantTitle || '';
      const img = cb.nextElementSibling?.querySelector('img');
      const imgSrc = img?.src || '';

      const swatchItem = document.createElement('div');
      swatchItem.className = 'expandable-swatch-item flex items--center p1 bg--white';
      swatchItem.innerHTML = `
        ${imgSrc ? `<img src="${imgSrc}" alt="${variantTitle}">` : ''}
        <div>
          <div class="h6">${productTitle}</div>
          <div class="caption color--grey2">${variantTitle}</div>
        </div>
      `;
      this.elements.selectedList.appendChild(swatchItem);
    });
  }

  populateEmptySlots(checkedCount) {
    if (!this.elements.emptySlots) return;

    this.elements.emptySlots.innerHTML = '';
    const emptyCount = MAX_SELECTIONS - checkedCount;

    for (let i = 0; i < emptyCount; i++) {
      const slot = document.createElement('div');
      slot.className = 'expandable-empty-slot flex items--center justify--center color--grey2';
      slot.textContent = 'Empty slot';
      this.elements.emptySlots.appendChild(slot);
    }
  }

  updateButtons(count, addToCartCallback) {
    const isValid = count >= 1 && count <= MAX_SELECTIONS;

    if (this.elements.addBtn) {
      this.elements.addBtn.disabled = !isValid;
    }

    if (this.elements.addBtnFooter) {
      this.elements.addBtnFooter.disabled = !isValid;

      // Set up callback (only once)
      if (addToCartCallback && !this.elements.addBtnFooter._listenerAdded) {
        this.elements.addBtnFooter.addEventListener('click', addToCartCallback);
        this.elements.addBtnFooter._listenerAdded = true;
      }
    }
  }
}

/**
 * RugSwatchesDrawer Class
 * Manages the drawer component for rug swatch selection
 */
class RugSwatchesDrawer {
  constructor() {
    this.hasOpenedOnce = false;

    // Move drawer and overlay to body to escape #MainContent stacking context
    const drawerEl = document.getElementById('rug-swatches-drawer');
    const overlayEl = document.getElementById('rug-swatches-drawer-overlay');
    if (drawerEl) document.body.appendChild(drawerEl);
    if (overlayEl) document.body.appendChild(overlayEl);

    this.elements = {
      drawer: document.getElementById('rug-swatches-drawer'),
      overlay: document.getElementById('rug-swatches-drawer-overlay'),
      openBtns: document.querySelectorAll('[data-drawer-open="rug-swatches-drawer"]'),
      closeBtn: document.querySelector('[data-drawer-close="rug-swatches-drawer"]'),
      checkboxes: document.querySelectorAll('.drawer-variant-checkbox'),
      selectedCountFooter: document.getElementById('drawer-selected-count'),
      addBundleBtn: document.getElementById('drawer-add-btn'),
      stickyFooter: document.querySelector('.drawer-sticky-footer'),
      footerToggle: document.getElementById('drawer-footer-toggle'),
      footerCollapse: document.getElementById('drawer-footer-collapse')
    };

    this.init();
  }

  init() {
    if (!this.elements.drawer || !this.elements.openBtns.length) return;

    // Drawer controls
    this.elements.openBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.open(e));
    });
    this.elements.closeBtn?.addEventListener('click', () => this.close());
    this.elements.overlay.addEventListener('click', () => this.close());

    // Footer toggle
    this.elements.footerToggle?.addEventListener('click', () => this.toggleFooter());
    this.elements.footerCollapse?.addEventListener('click', () => this.toggleFooter());

    // Checkbox listeners
    this.elements.checkboxes.forEach(cb => {
      cb.addEventListener('change', () => this.updateUI());
    });

    // Add to cart
    this.elements.addBundleBtn.addEventListener('click', (e) => this.addToCart(e));
    document.getElementById('drawer-add-btn-footer')?.addEventListener('click', (e) => this.addToCart(e));

    // Initialize accordions
    this.initAccordions();
    this.updateUI();
  }

  open(e) {
    e.preventDefault();
    this.elements.drawer.classList.add('is-open');
    this.elements.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Hide mobile add to cart button
    const mobAddToCart = document.getElementById('mobAddToCart');
    if (mobAddToCart) mobAddToCart.style.display = 'none';

    // The drawer lives in #MainContent: drop the sticky header under it while open
    document.documentElement.setAttribute('data-hdr-under', '');

    setTimeout(() => this.initAccordions(), 50);
  }

  close() {
    this.elements.drawer.classList.remove('is-open');
    this.elements.overlay.classList.remove('is-open');
    document.body.style.overflow = '';

    // Show mobile add to cart button
    const mobAddToCart = document.getElementById('mobAddToCart');
    if (mobAddToCart) mobAddToCart.style.display = '';

    document.documentElement.removeAttribute('data-hdr-under');
  }

  toggleFooter() {
    if (!this.elements.stickyFooter) return;

    const isExpanding = !this.elements.stickyFooter.classList.contains('expanded');
    this.elements.stickyFooter.classList.toggle('expanded');

    if (isExpanding && !this.hasOpenedOnce) {
      this.elements.stickyFooter.classList.add('first-open');
      this.hasOpenedOnce = true;
      setTimeout(() => this.elements.stickyFooter.classList.remove('first-open'), 1000);
    }
  }

  updateUI() {
    const checked = document.querySelectorAll('.drawer-variant-checkbox:checked');
    const checkedCount = checked.length;

    // Update footer count
    if (this.elements.selectedCountFooter) {
      this.elements.selectedCountFooter.textContent = `${checkedCount}/${MAX_SELECTIONS} SWATCHES SELECTED`;
    }

    // Toggle max-reached class
    this.elements.checkboxes.forEach(cb => {
      cb.nextElementSibling.classList.toggle('variant-max-reached', !cb.checked && checkedCount >= MAX_SELECTIONS);
    });

    // Update button states
    const isValid = checkedCount >= 1 && checkedCount <= MAX_SELECTIONS;
    this.elements.addBundleBtn.disabled = !isValid;

    // Update drawer footer
    this.updateDrawerFooter(checked, checkedCount);
  }

  updateDrawerFooter(checked, checkedCount) {
    // Update count texts
    const drawerCountText = document.getElementById('drawer-count-text');
    const drawerFooterCount = document.getElementById('drawer-footer-count');

    if (drawerCountText) drawerCountText.textContent = checkedCount;
    if (drawerFooterCount) {
      drawerFooterCount.textContent = `${checkedCount}/${MAX_SELECTIONS} SWATCHES SELECTED`;
    }

    // Populate selected list
    this.populateSelectedList(checked);

    // Populate empty slots
    this.populateEmptySlots(checkedCount);

    // Update footer button
    const addBtnFooter = document.getElementById('drawer-add-btn-footer');
    if (addBtnFooter) {
      addBtnFooter.disabled = !(checkedCount >= 1 && checkedCount <= MAX_SELECTIONS);
    }
  }

  populateSelectedList(checked) {
    const drawerSelectedList = document.getElementById('drawer-selected-list');
    if (!drawerSelectedList) return;

    drawerSelectedList.innerHTML = '';
    checked.forEach(cb => {
      const productTitle = cb.dataset.productTitle || '';
      const variantTitle = cb.dataset.variantTitle || '';
      const img = cb.nextElementSibling?.querySelector('img');
      const imgSrc = img?.src || '';

      const swatchItem = document.createElement('div');
      swatchItem.className = 'drawer-swatch-item flex items--center border-bottom--oat';
      swatchItem.innerHTML = `
        <div class="drawer-swatch-image overflow--hidden mr1">
          <img class="full img--cover" src="${imgSrc}" alt="${variantTitle}">
        </div>
        <div class="drawer-swatch-info flex--1">
          <div class="h4 f--heading drawer-swatch-title mb0">${productTitle}</div>
          <div class="drawer-swatch-subtitle color--grey2 mb0">${variantTitle}</div>
        </div>
        <div class="drawer-swatch-remove caption pointer nowrap color--grey2 self--end pb1" data-variant-id="${cb.value}">REMOVE</div>
      `;
      drawerSelectedList.appendChild(swatchItem);

      // Add remove button functionality
      swatchItem.querySelector('.drawer-swatch-remove')?.addEventListener('click', () => {
        cb.checked = false;
        cb.dispatchEvent(new Event('change'));
      });
    });
  }

  populateEmptySlots(checkedCount) {
    const drawerEmptySlots = document.getElementById('drawer-empty-slots');
    if (!drawerEmptySlots) return;

    drawerEmptySlots.innerHTML = '';
    const emptyCount = MAX_SELECTIONS - checkedCount;
    const plusIconHTML = `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 6V24" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      <path d="M6 15H24" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`;

    for (let i = 0; i < emptyCount; i++) {
      const slot = document.createElement('div');
      slot.className = 'drawer-empty-slot flex items--center border-bottom--oat';
      slot.innerHTML = `
        <div class="drawer-empty-slot-icon flex items--center justify--center bg--white mr1">
          <span class="slot-plus flex">${plusIconHTML}</span>
        </div>
      `;
      drawerEmptySlots.appendChild(slot);
    }
  }

  async addToCart(event) {
    const checked = document.querySelectorAll('.drawer-variant-checkbox:checked');

    if (checked.length < 1 || checked.length > MAX_SELECTIONS) {
      alert(`Please select between 1 and ${MAX_SELECTIONS} items.`);
      return;
    }

    const bundleId = `rugs-${Date.now()}`;
    const items = Array.from(checked).map(input => ({
      id: input.value,
      quantity: 1,
      properties: {
        _rug_swatches_bundle: bundleId,
        Bundle: 'Rug Swatches Bundle'
      }
    }));

    // Disable buttons
    const buttons = [
      this.elements.addBundleBtn,
      document.getElementById('drawer-add-btn-footer')
    ].filter(Boolean);

    buttons.forEach(btn => {
      btn.disabled = true;
      btn.textContent = 'Adding...';
    });

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items })
      });

      const data = await res.json();

      if (res.ok) {
        // Store swatch count for insurance calculation
        localStorage.setItem('rug_swatches_count', checked.length);

        // Reset form
        this.elements.checkboxes.forEach(cb => {
          cb.checked = false;
          cb.nextElementSibling.classList.remove('variant-max-reached');
        });
        this.updateUI();
        this.close();

        // Reload cart
        setTimeout(() => {
          if (typeof ajaxCart !== 'undefined' && ajaxCart.load) {
            ajaxCart.load();
          } else {
            window.location.href = '/cart';
          }
        }, 100);
      } else {
        console.error("Failed to add bundle to cart. Status:", res.status, "Response:", data);
        alert("Failed to add bundle to cart. Please try again.");
      }
    } catch (error) {
      console.error("Error adding bundle to cart:", error);
      alert("Failed to add bundle to cart. Please try again.");
    } finally {
      buttons.forEach(btn => {
        btn.disabled = false;
        btn.textContent = 'ADD TO CART';
      });
    }
  }

  initAccordions() {
    const drawerAccordions = document.querySelectorAll('.accordions-container-drawer .accordian-wrapper');

    drawerAccordions.forEach(accordion => {
      const header = accordion.querySelector('.accordian-header');
      if (!header) return;

      // Remove existing handlers by cloning
      const newHeader = header.cloneNode(true);
      header.parentNode.replaceChild(newHeader, header);

      // Add fresh click handler
      newHeader.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        const wrapper = this.parentElement;
        const isActive = wrapper.classList.contains('active');

        wrapper.classList.toggle('active');
        wrapper.setAttribute('aria-expanded', !isActive ? 'true' : 'false');
      });
    });
  }
}

/**
 * Initialize on DOM ready
 */
function initRugSwatches() {
  // Initialize drawer if it exists
  if (document.getElementById('rug-swatches-drawer')) {
    new RugSwatchesDrawer();
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRugSwatches);
} else {
  initRugSwatches();
}

// Export for manual initialization if needed
window.RugSwatchesSystem = {
  ExpandableFooter,
  RugSwatchesDrawer,
  init: initRugSwatches
};
